import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import {
  CreditCard, MapPin, MessageCircle, Package, PackageSearch, RefreshCw, Search, ShoppingCart, SlidersHorizontal, Store, Truck, X,
} from 'lucide-react';
import { escucharCatalogoPublico } from '../../aplicacion/casosCatalogoPublico';
import { construirUrlWhatsApp } from '../../dominio/logicaMensajes';
import { esCobroAutomatico, nombrePasarela } from '../../dominio/pasarelas';
import {
  categoriasConConteo, marcasConConteo, claveLinea, filtrarProductos, MAX_LINEAS_PEDIDO, MAX_UNIDADES_LINEA, mediosPagoTienda, ORDENES_LISTADO,
  ordenarProductos, soles, stockDeSeleccion, unidadesAgregables,
} from '../../dominio/logicaTiendaPublica';
import { aplicarTema, CLAVE_TEMA_GUARDADO } from '../../utilidades/temaVisual';
import { aplicarIdentidadTienda } from '../../utilidades/brandingDocumento';
import { guardarCarrito, guardarFavoritos, guardarMarcaTienda, leerCarrito, leerFavoritos } from '../../utilidades/carritoLocal';
import { CarritoCatalogo } from './CarritoCatalogo';
import { EstadoHorarioCatalogo, HorarioSemanalCatalogo } from './EstadoHorarioCatalogo';
import { TarjetaProducto } from './tiendaPublica/TarjetaProducto';
import { DetalleProducto } from './tiendaPublica/DetalleProducto';
import { LogosPago } from './tiendaPublica/LogosPago';
import { LogoTienda } from './tiendaPublica/LogoTienda';
import { aplicarSeoTienda, datosSeoTienda } from '../../utilidades/seoTienda';
import { origenPublico } from '../../utilidades/origenPublico';
import { establecerMoneda } from '../../dominio/moneda';
import '../../estilos/tiendaPublica.css';

const celularVisible = numero => String(numero || '').replace(/^51/, '+51 ');
/** Tras 5 min con la pestaña oculta se cierran los listeners (0 lecturas por
 *  cambios de stock en pestañas olvidadas); al volver se reabren. */
const PAUSA_OCULTA_MS = 5 * 60 * 1000;
/** Al volver a la pestaña tras 1 min oculta se pide el catálogo de nuevo: el
 *  comprador ve precios y stock al día (desde el CDN, sin lecturas de Firestore). */
const REFRESCO_AL_VOLVER_MS = 60 * 1000;
const ETIQUETAS_METODO = { yape: 'Yape', plin: 'Plin', transferencia: 'Transferencia', tarjeta: 'Tarjeta' };

const PanelFiltros = ({ categorias, categoria, setCategoria, marcas = [], marca, setMarca, soloConStock, setSoloConStock, soloFavoritos, setSoloFavoritos, totalFavoritos }) => <>
  <section className="tp-filtro-grupo">
    <h2>Categorías</h2>
    <ul>
      {categorias.map(c => <li key={c.nombre}>
        <button type="button" aria-pressed={categoria === c.nombre} onClick={() => setCategoria(c.nombre)}>
          {c.nombre}<span aria-hidden="true">{c.total}</span>
        </button>
      </li>)}
    </ul>
  </section>
  {marcas.length > 0 && <section className="tp-filtro-grupo">
    <h2>Marcas</h2>
    <ul>
      {marcas.map(m => <li key={m.nombre}>
        <button type="button" aria-pressed={marca === m.nombre} onClick={() => setMarca(m.nombre)}>
          {m.nombre}<span aria-hidden="true">{m.total}</span>
        </button>
      </li>)}
    </ul>
  </section>}
  <section className="tp-filtro-grupo">
    <h2>Mostrar</h2>
    <label className="tp-check"><input type="checkbox" checked={soloConStock} onChange={e => setSoloConStock(e.target.checked)} /> Solo con stock</label>
    <label className="tp-check"><input type="checkbox" checked={soloFavoritos} onChange={e => setSoloFavoritos(e.target.checked)} /> Mis favoritos ({totalFavoritos})</label>
  </section>
</>;

// Último catálogo recibido por gym (memoria de la pestaña): al volver de «Mis pedidos»
// se muestra al instante mientras el listener se reconecta, sin pantalla de carga.
const catalogosRecientes = new Map();
// Olvida los catálogos recordados (pruebas).
export const limpiarCatalogosRecientes = () => catalogosRecientes.clear();

const PaginaCatalogo = ({ gymId }) => {
  const [catalogo, setCatalogo] = useState(() => catalogosRecientes.get(gymId) || null);
  // Moneda de la tienda (la del gym al publicar): precios y carrito con su símbolo.
  establecerMoneda(catalogo?.moneda);
  const [cargando, setCargando] = useState(() => !catalogosRecientes.has(gymId)), [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [busqueda, setBusqueda] = useState(''), [categoria, setCategoria] = useState('Todos'), [marca, setMarca] = useState('Todas');
  const [orden, setOrden] = useState('recomendados');
  const [soloConStock, setSoloConStock] = useState(false), [soloFavoritos, setSoloFavoritos] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [carrito, setCarrito] = useState(() => leerCarrito(gymId));
  const [favoritos, setFavoritos] = useState(() => leerFavoritos(gymId));
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [detalleId, setDetalleId] = useState(null);
  const [retornoMp, setRetornoMp] = useState(() => new URLSearchParams(window.location.search).get('retorno') === 'mp');
  const botonCarrito = useRef(null), cerrarCajon = useRef(null);

  // ── Catálogo en vivo (cabecera + productos), con pausa en pestañas ocultas ──
  useEffect(() => {
    let vigente = true, cancelar = null, pausa = null, ocultaDesde = 0;
    const suscribir = () => {
      cancelar = escucharCatalogoPublico(gymId, resultado => {
        if (!vigente) return;
        if (resultado) catalogosRecientes.set(gymId, resultado); else catalogosRecientes.delete(gymId);
        setCatalogo(resultado); setCargando(false); setError('');
      }, err => {
        if (!vigente) return;
        catalogosRecientes.delete(gymId);
        setCatalogo(null); setCargando(false);
        setError(err?.code === 'permission-denied' ? 'Este catálogo no está disponible.' : 'No pudimos cargar el catálogo. Revisa tu conexión e inténtalo nuevamente.');
      });
    };
    const alCambiarVisibilidad = () => {
      clearTimeout(pausa);
      if (document.visibilityState === 'hidden') {
        ocultaDesde = Date.now();
        pausa = setTimeout(() => { if (cancelar) { cancelar(); cancelar = null; } }, PAUSA_OCULTA_MS);
      } else if (vigente) {
        const volvioTrasPausa = ocultaDesde > 0 && Date.now() - ocultaDesde >= REFRESCO_AL_VOLVER_MS;
        ocultaDesde = 0;
        if (cancelar && volvioTrasPausa) { cancelar(); cancelar = null; }
        if (!cancelar) suscribir();
      }
    };
    setCargando(!catalogosRecientes.has(gymId)); setError('');
    suscribir();
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      vigente = false; clearTimeout(pausa);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      if (cancelar) cancelar();
    };
  }, [gymId, revision]);
  const reintentar = useCallback(() => setRevision(valor => valor + 1), []);

  // SEO: título, descripción, canonical, Open Graph y schema.org de esta tienda.
  useEffect(() => {
    if (!catalogo) {
      const tituloAnterior = document.title;
      document.title = 'Tienda del gimnasio';
      return () => { document.title = tituloAnterior; };
    }
    return aplicarSeoTienda(datosSeoTienda({ catalogo, gymId, origen: origenPublico() }));
  }, [catalogo, gymId]);

  // ── Marca del catálogo, siempre en modo claro, sin tocar el tema del panel ──
  const firmaMarca = catalogo ? JSON.stringify([catalogo.nombre, catalogo.marca, catalogo.whatsapp, catalogo.metodosPago]) : '';
  useEffect(() => {
    if (!catalogo) return;
    aplicarTema({ primario: catalogo.marca?.colorPrimario, secundario: catalogo.marca?.colorSecundario, modo: 'claro' }, { persistir: false });
    guardarMarcaTienda(gymId, catalogo);
    aplicarIdentidadTienda({ gymId, nombre: catalogo.nombre, logoBase64: catalogo.marca?.logoBase64, colorPrimario: catalogo.marca?.colorPrimario });
  }, [firmaMarca]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    try {
      const guardado = JSON.parse(localStorage.getItem(CLAVE_TEMA_GUARDADO) || 'null');
      if (guardado) aplicarTema(guardado, { persistir: false });
    } catch { /* sin tema guardado */ }
  }, []);

  // ── Carrito y favoritos locales (0 lecturas) ──
  useEffect(() => { guardarCarrito(gymId, carrito); }, [gymId, carrito]);
  useEffect(() => { guardarFavoritos(gymId, favoritos); }, [gymId, favoritos]);

  const cambiarCantidad = useCallback((clave, cantidad) => {
    const limpia = Math.min(MAX_UNIDADES_LINEA, Math.max(0, Number(cantidad) || 0));
    setCarrito(actual => {
      const cantidades = { ...actual.cantidades }, variantes = { ...actual.variantes };
      if (limpia) cantidades[clave] = limpia; else { delete cantidades[clave]; delete variantes[clave]; }
      return { cantidades, variantes };
    });
  }, []);

  // El aviso «agregado» no debe tapar la cabecera del carrito recién abierto.
  const abrirCarrito = useCallback(() => { toast.dismiss(); setCarritoAbierto(true); }, []);
  const agregar = (producto, cantidad, seleccion) => {
    const clave = claveLinea(producto.id, seleccion.talla, seleccion.color);
    const enCarrito = carrito.cantidades[clave] || 0;
    if (!enCarrito && Object.keys(carrito.cantidades).length >= MAX_LINEAS_PEDIDO) {
      toast.error(`Tu pedido admite hasta ${MAX_LINEAS_PEDIDO} productos distintos.`);
      return;
    }
    const stock = stockDeSeleccion(producto, seleccion);
    const posibles = unidadesAgregables({ stock, enCarrito });
    if (posibles <= 0) {
      toast.error(stock > 0 ? `Ya tienes en tu carrito todo el stock disponible de ${producto.nombre}.` : `${producto.nombre} está agotado.`);
      return;
    }
    const agregadas = Math.min(cantidad, posibles);
    setCarrito(actual => ({
      cantidades: { ...actual.cantidades, [clave]: (actual.cantidades[clave] || 0) + agregadas },
      variantes: { ...actual.variantes, [clave]: { ...seleccion, productoId: producto.id } },
    }));
    toast.success(agregadas < cantidad ? `Se agregaron ${agregadas}: es el stock disponible.` : `${producto.nombre} se agregó al carrito`,
      { action: { label: 'Ver carrito', onClick: abrirCarrito } });
  };

  const alternarFavorito = (id) => setFavoritos(actual => {
    const siguiente = new Set(actual);
    if (siguiente.has(id)) siguiente.delete(id); else siguiente.add(id);
    return siguiente;
  });

  // ── Cajón del carrito: foco, Escape y bloqueo del scroll del fondo ──
  useEffect(() => {
    if (!carritoAbierto) return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cerrarCajon.current?.focus?.();
    return () => { document.body.style.overflow = anterior; };
  }, [carritoAbierto]);
  const cerrarCarrito = () => { setCarritoAbierto(false); botonCarrito.current?.focus?.(); };

  // ── Detalle compartible: #p-{id} ──
  useEffect(() => {
    if (!catalogo) return;
    const id = decodeURIComponent((window.location.hash.match(/^#p-(.+)$/) || [])[1] || '');
    if (id && catalogo.productos.some(p => p.id === id)) setDetalleId(id);
  }, [catalogo?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const abrirDetalle = (producto) => {
    setDetalleId(producto.id);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#p-${encodeURIComponent(producto.id)}`);
  };
  const cerrarDetalle = () => {
    setDetalleId(null);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  };

  const categorias = useMemo(() => catalogo ? categoriasConConteo(catalogo.productos) : [{ nombre: 'Todos', total: 0 }], [catalogo]);
  useEffect(() => {
    if (categoria !== 'Todos' && !categorias.some(c => c.nombre === categoria)) setCategoria('Todos');
  }, [categorias, categoria]);
  const marcas = useMemo(() => catalogo ? marcasConConteo(catalogo.productos) : [], [catalogo]);
  useEffect(() => {
    if (marca !== 'Todas' && !marcas.some(m => m.nombre === marca)) setMarca('Todas');
  }, [marcas, marca]);
  const productos = useMemo(() => !catalogo ? [] : ordenarProductos(filtrarProductos(catalogo.productos, {
    busqueda, categoria, marca, soloConStock, favoritos: soloFavoritos ? favoritos : null,
  }), orden), [catalogo, busqueda, categoria, marca, soloConStock, soloFavoritos, favoritos, orden]);
  const hayFiltros = busqueda || categoria !== 'Todos' || marca !== 'Todas' || soloConStock || soloFavoritos;
  const limpiarFiltros = () => { setBusqueda(''); setCategoria('Todos'); setMarca('Todas'); setSoloConStock(false); setSoloFavoritos(false); };

  const unidades = Object.values(carrito.cantidades).reduce((s, n) => s + Number(n || 0), 0);
  const totalCarrito = catalogo ? Object.entries(carrito.cantidades).reduce((s, [clave, n]) => {
    const p = catalogo.productos.find(x => x.id === (carrito.variantes[clave]?.productoId || clave));
    return s + (p ? Number(p.precio) * n : 0);
  }, 0) : 0;
  const enCarritoDe = (id) => Object.entries(carrito.cantidades).reduce((s, [clave, n]) => s + ((carrito.variantes[clave]?.productoId || clave) === id ? n : 0), 0);
  const ayuda = catalogo ? construirUrlWhatsApp(catalogo.whatsapp, `Hola ${catalogo.nombre}, necesito ayuda con la tienda online.`, '') : '#';
  const pasarela = esCobroAutomatico(catalogo?.modoCobro);
  const sufijoPrueba = catalogo?.modoCobro === 'automatico_pruebas' ? ' (pruebas)' : '';
  const nombrePago = nombrePasarela(catalogo?.proveedorCobro || 'mercadopago');
  const medios = mediosPagoTienda({ automatico: pasarela, proveedor: catalogo?.proveedorCobro || 'mercadopago', metodosPago: catalogo?.metodosPago });
  const detalle =detalleId && catalogo ? catalogo.productos.find(p => p.id === detalleId) : null;
  const propsFiltros = { categorias, categoria, setCategoria, marcas, marca, setMarca, soloConStock, setSoloConStock, soloFavoritos, setSoloFavoritos, totalFavoritos: favoritos.size };

  if (cargando && !catalogo) {
    return <div className="tienda-publica"><main className="tp-estado-carga" role="status" aria-label="Cargando catálogo">
      <span className="ag-solo-lector">Cargando catálogo…</span>
      <div className="tp-esqueleto-barra" aria-hidden="true" />
      <div className="tp-grilla tp-grilla--esqueleto" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <div key={i} className="tp-esqueleto-tarjeta" />)}</div>
    </main></div>;
  }

  if (!catalogo) {
    return <div className="tienda-publica"><main className="tp-no-disponible">
      <Package size={44} aria-hidden="true" /><h1>Catálogo no disponible</h1>
      <p role={error ? 'alert' : undefined}>{error || 'El gimnasio todavía no ha publicado su tienda.'}</p>
      <button type="button" className="tp-boton tp-boton--secundario" onClick={reintentar}><RefreshCw size={17} aria-hidden="true" /> Reintentar</button>
    </main></div>;
  }

  return <div className="tienda-publica">
    <div className="tp-utilidad">
      <div className="tp-contenedor tp-utilidad-fila">
        <EstadoHorarioCatalogo horarioSemanal={catalogo.horarioSemanal} horario={catalogo.horario} />
        {catalogo.direccion && <span className="tp-utilidad-retiro"><MapPin size={14} aria-hidden="true" /> Retiro: {catalogo.direccion}</span>}
        <a href={ayuda} target="_blank" rel="noreferrer"><MessageCircle size={14} aria-hidden="true" /> WhatsApp <span className="tp-utilidad-numero">{celularVisible(catalogo.whatsapp)}</span></a>
      </div>
    </div>

    <header className="tp-cabecera">
      <div className="tp-barra-marca">
        <div className="tp-contenedor tp-barra-marca-fila">
          <a href="#productos" className="tp-marca">
            {catalogo.marca?.logoBase64 ? <LogoTienda logo={catalogo.marca.logoBase64} /> : <span className="tp-marca-icono"><Store size={22} aria-hidden="true" /></span>}
            <strong>{catalogo.nombre}</strong>
          </a>
          <label className="tp-buscador">
            <Search size={18} aria-hidden="true" />
            <input aria-label="Buscar productos" type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar productos o categorías" enterKeyHint="search" />
            {busqueda && <button type="button" className="tp-buscador-limpiar" onClick={() => setBusqueda('')} aria-label="Borrar búsqueda"><X size={16} aria-hidden="true" /></button>}
          </label>
          <Link className="tp-boton-carrito tp-enlace-pedidos" to={`/catalogo/${gymId}/pedidos`} aria-label="Mis pedidos">
            <PackageSearch size={22} aria-hidden="true" /><span className="tp-boton-carrito-texto"><strong>Mis pedidos</strong></span>
          </Link>
          <button type="button" ref={botonCarrito} className="tp-boton-carrito" aria-expanded={carritoAbierto} aria-controls="tp-cajon-carrito"
            aria-label={`Carrito, ${unidades} ${unidades === 1 ? 'unidad' : 'unidades'}`} onClick={abrirCarrito}>
            <span className="tp-boton-carrito-icono"><ShoppingCart size={22} aria-hidden="true" />{unidades > 0 && <b>{unidades}</b>}</span>
            <span className="tp-boton-carrito-texto"><span>Carrito</span><strong>{soles(totalCarrito)}</strong></span>
          </button>
        </div>
      </div>
      <nav className="tp-departamentos" aria-label="Categorías del catálogo">
        <div className="tp-contenedor tp-departamentos-fila">
          {categorias.map(c => <button key={c.nombre} type="button" aria-pressed={categoria === c.nombre}
            className={categoria === c.nombre ? 'is-activo' : ''} onClick={() => setCategoria(c.nombre)}>{c.nombre}</button>)}
        </div>
      </nav>
    </header>

    {retornoMp && <div className="tp-contenedor"><p className="tp-aviso tp-aviso--info tp-aviso-retorno" role="status">
      <CreditCard size={16} aria-hidden="true" />
      <span>Volviste de Mercado Pago. Volver no confirma el pago por sí solo: consulta el estado de tu pedido.
        <button type="button" className="tp-boton-texto" onClick={abrirCarrito}>Ver mi pedido</button></span>
      <button type="button" className="tp-icono-boton" aria-label="Cerrar aviso" onClick={() => {
        setRetornoMp(false);
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);
      }}><X size={16} aria-hidden="true" /></button>
    </p></div>}

    <main className="tp-principal" id="productos">
      <div className="tp-contenedor">
        <section className="tp-presentacion" aria-labelledby="tp-titulo-tienda">
          <div>
            <h1 id="tp-titulo-tienda">Tienda de {catalogo.nombre}</h1>
            <p>{catalogo.descripcion || 'Productos publicados por el gimnasio, con atención directa por WhatsApp.'}</p>
          </div>
          <ul className="tp-servicios" aria-label="Cómo comprar">
            <li><Store size={18} aria-hidden="true" /><span><strong>Retiro en tienda</strong>Gratis, en la sede</span></li>
            {catalogo.deliveryHabilitado && <li><Truck size={18} aria-hidden="true" /><span><strong>Delivery</strong>Tarifa por coordinar</span></li>}
            <li><CreditCard size={18} aria-hidden="true" /><span>{pasarela
              ? <><strong>Pago en línea</strong>{nombrePago}{sufijoPrueba}</>
              : <><strong>Pagas al confirmar</strong>{(catalogo.metodosPago || []).filter(m => ETIQUETAS_METODO[m]).map(m => ETIQUETAS_METODO[m]).join(', ') || 'Coordinado con el gimnasio'}</>}</span></li>
          </ul>
        </section>

        <div className="tp-catalogo">
          <aside className="tp-filtros" aria-label="Filtros"><PanelFiltros {...propsFiltros} /></aside>

          <section className="tp-resultados" aria-label="Productos">
            <div className="tp-resultados-cabecera">
              <p aria-live="polite"><strong>{productos.length}</strong> {productos.length === 1 ? 'producto' : 'productos'}</p>
              <button type="button" className="tp-boton tp-boton--secundario tp-boton-filtrar" onClick={() => setFiltrosAbiertos(true)}>
                <SlidersHorizontal size={16} aria-hidden="true" /> Filtrar
              </button>
              <label className="tp-ordenar"><span>Ordenar por</span>
                <select value={orden} onChange={e => setOrden(e.target.value)}>
                  {ORDENES_LISTADO.map(o => <option key={o.id} value={o.id}>{o.etiqueta}</option>)}
                </select>
              </label>
            </div>

            {productos.length > 0 ? <div className="tp-grilla">
              {productos.map(p => <TarjetaProducto key={`${catalogo.version}_${p.id}`} producto={p} enCarrito={enCarritoDe(p.id)}
                favorito={favoritos.has(p.id)} onFavorito={alternarFavorito} onAgregar={agregar} onAbrir={abrirDetalle} />)}
            </div> : <div className="tp-vacio">
              <Search size={28} aria-hidden="true" />
              <p><strong>No hay productos que coincidan con tu búsqueda.</strong>Prueba con otra palabra o quita los filtros.</p>
              {hayFiltros && <button type="button" className="tp-boton tp-boton--secundario" onClick={limpiarFiltros}>Limpiar filtros</button>}
            </div>}

            {catalogo.deliveryHabilitado && <p className="tp-aviso tp-aviso--neutro tp-delivery"><Truck size={18} aria-hidden="true" />
              <span><strong>¿Necesitas envío a domicilio?</strong> {catalogo.notaDelivery || 'La cobertura y la tarifa se coordinan con el gimnasio antes del pago.'}</span></p>}
          </section>
        </div>
      </div>
    </main>

    <footer className="tp-pie">
      <div className="tp-contenedor tp-pie-columnas">
        <div className="tp-pie-marca">
          {catalogo.marca?.logoBase64 && <img src={catalogo.marca.logoBase64} alt="" />}
          <strong>{catalogo.nombre}</strong>
          <p>Venta y atención directa del gimnasio.</p>
        </div>
        <div>
          <h2>Atención</h2>
          <p>{catalogo.direccion || 'Dirección coordinada por WhatsApp'}</p>
          <HorarioSemanalCatalogo horarioSemanal={catalogo.horarioSemanal} horario={catalogo.horario} />
          <a href={ayuda} target="_blank" rel="noreferrer"><MessageCircle size={15} aria-hidden="true" /> Escríbenos por WhatsApp</a>
        </div>
        <div>
          <h2>Cómo comprar</h2>
          {pasarela ? <ol>
            <li>Agrega productos al carrito.</li><li>Envía tu pedido: reservamos tu stock.</li>
            <li>Paga en línea con {nombrePago}{sufijoPrueba}.</li><li>Retira o recibe cuando te confirmen.</li>
          </ol> : <ol>
            <li>Agrega productos al carrito y envía tu pedido.</li><li>El gimnasio confirma stock y total.</li>
            <li>Paga y reporta tu N.º de operación.</li><li>Retira o recibe cuando te confirmen.</li>
          </ol>}
        </div>
        {medios.length > 0 && <div>
          <h2>Medios de pago</h2>
          <LogosPago medios={medios} />
          {pasarela && <p className="tp-pie-nota">Pago seguro con {nombrePago}.</p>}
        </div>}
      </div>
      <p className="tp-contenedor tp-pie-legal">{pasarela
        ? `Al enviar el pedido se reserva el stock y se abre el pago en línea${sufijoPrueba ? ' de prueba' : ''}. Disponibilidad sujeta a confirmación.`
        : 'Enviar el pedido no reserva stock ni cobra: el gimnasio confirma disponibilidad y total antes del pago.'}</p>
    </footer>

    {unidades > 0 && !carritoAbierto && <button type="button" className="tp-barra-movil" onClick={abrirCarrito}>
      <ShoppingCart size={18} aria-hidden="true" /><span>Ver carrito · {unidades}</span><strong>{soles(totalCarrito)}</strong>
    </button>}

    <div className="tp-cajon-capa" hidden={!carritoAbierto}>
      <div className="tp-velo" onClick={cerrarCarrito} aria-hidden="true" />
      <div id="tp-cajon-carrito" className="tp-cajon" role="dialog" aria-modal="true" aria-label="Carrito"
        onKeyDown={e => { if (e.key === 'Escape') cerrarCarrito(); }}>
        <div className="tp-cajon-barra">
          <button type="button" ref={cerrarCajon} className="tp-icono-boton" onClick={cerrarCarrito} aria-label="Cerrar carrito"><X size={20} aria-hidden="true" /></button>
        </div>
        <CarritoCatalogo gymId={gymId} catalogo={catalogo} cantidades={carrito.cantidades} variantes={carrito.variantes} cambiarCantidad={cambiarCantidad}
          onCatalogoDesactualizado={reintentar} />
      </div>
    </div>

    <Dialog.Root open={filtrosAbiertos} onOpenChange={setFiltrosAbiertos}>
      <Dialog.Portal>
        <Dialog.Overlay className="tp-velo tp-velo--fijo" />
        <Dialog.Content className="tp-hoja-filtros" aria-describedby={undefined}>
          <div className="tp-hoja-cabecera"><Dialog.Title>Filtrar</Dialog.Title>
            <Dialog.Close className="tp-icono-boton" aria-label="Cerrar filtros"><X size={20} aria-hidden="true" /></Dialog.Close></div>
          <PanelFiltros {...propsFiltros} />
          <div className="tp-hoja-pie">
            <button type="button" className="tp-boton tp-boton--secundario" onClick={limpiarFiltros}>Limpiar</button>
            <Dialog.Close className="tp-boton tp-boton--primario">Ver {productos.length} productos</Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

    {detalle && <DetalleProducto key={detalle.id} producto={detalle} enCarrito={enCarritoDe(detalle.id)} favorito={favoritos.has(detalle.id)}
      onFavorito={alternarFavorito} onAgregar={agregar} onCerrar={cerrarDetalle} />}
  </div>;
};

// No monta GymProvider/TiendaProvider: no lee inventario privado ni datos del admin.
export const CatalogoPublico = () => {
  const { gymId } = useParams();
  return <PaginaCatalogo key={gymId} gymId={gymId} />;
};

